import React, { useEffect, useRef } from 'react';
import 'quill/dist/quill.snow.css';

// Quill ships modern .d.ts that TypeScript 3.9 (this project's version) can't
// parse, so it is pulled in through require() and typed as `any` on purpose.
// eslint-disable-next-line @typescript-eslint/no-var-requires
const QuillLib: any = require('quill');
const Quill: any = QuillLib.default || QuillLib;

const TOOLBAR = [
  [{ header: [2, 3, 4, false] }],
  ['bold', 'italic', 'underline', 'strike'],
  [{ list: 'ordered' }, { list: 'bullet' }],
  [{ align: [] }],
  ['blockquote', 'code-block'],
  ['link', 'image'],
  ['clean'],
];

// Quill leaves this behind for an empty document — treat it as empty string so
// "description is required" validation still works.
const EMPTY_HTML = '<p><br></p>';

// Text pasted from Word/Docs arrives with `color: rgb(0,0,0)` on every element,
// which is invisible on this dark editor and on the dark site that renders it.
// The toolbar deliberately has no colour picker, so any colour here is unwanted.
const COLOR_DECLARATION = /^\s*(color|background|background-color)\s*:/i;

function stripInlineColors(html: string) {
  return html
    .replace(/\sstyle\s*=\s*("([^"]*)"|'([^']*)')/gi, (_m, _q, dq, sq) => {
      const kept = ((dq ?? sq) || '')
        .split(';')
        .filter((decl: string) => decl.trim() && !COLOR_DECLARATION.test(decl))
        .join('; ')
        .trim();
      return kept ? ` style="${kept}"` : '';
    })
    .replace(/\s(color|bgcolor)\s*=\s*("[^"]*"|'[^']*'|[^\s>]+)/gi, '');
}

interface QuillEditorProps {
  value: string;
  onChange: (html: string) => void;
  placeholder?: string;
  minHeight?: number;
  /** POST endpoint that accepts `image` (multipart) and returns { url }. */
  imageUploadUrl?: string;
  /** Turns the API's relative path into an absolute URL for the <img> tag. */
  resolveImageUrl?: (url: string) => string;
}

export default function QuillEditor({
  value,
  onChange,
  placeholder = 'Write your post…',
  minHeight = 320,
  imageUploadUrl,
  resolveImageUrl = (u) => u,
}: QuillEditorProps) {
  const hostRef = useRef<HTMLDivElement>(null);
  const quillRef = useRef<any>(null);
  // Last HTML this editor emitted — lets us tell "parent sent new content" from
  // "parent echoed back what we just typed" and skip the needless re-render.
  const lastHtmlRef = useRef<string>(value);
  const onChangeRef = useRef(onChange);
  const uploadRef = useRef({ imageUploadUrl, resolveImageUrl });

  onChangeRef.current = onChange;
  uploadRef.current = { imageUploadUrl, resolveImageUrl };

  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;

    // Quill mutates the DOM it is given, so hand it a throwaway child. On
    // cleanup we wipe the host, which also removes the toolbar Quill inserted —
    // important under StrictMode's double-invoked effects.
    const editorEl = document.createElement('div');
    host.appendChild(editorEl);

    const quill = new Quill(editorEl, {
      theme: 'snow',
      placeholder,
      modules: { toolbar: { container: TOOLBAR } },
    });
    quillRef.current = quill;

    // Drop colour/highlight formats from anything pasted in (this also runs for
    // dangerouslyPasteHTML below, so saved posts get cleaned on open too).
    quill.clipboard.addMatcher(Node.ELEMENT_NODE, (_node: any, delta: any) => {
      delta.ops = delta.ops.map((op: any) => {
        if (!op.attributes) return op;
        const attributes = { ...op.attributes };
        delete attributes.color;
        delete attributes.background;
        return Object.keys(attributes).length ? { ...op, attributes } : { insert: op.insert };
      });
      return delta;
    });

    if (lastHtmlRef.current) {
      quill.clipboard.dangerouslyPasteHTML(stripInlineColors(lastHtmlRef.current));
    }

    // Upload images to the backend instead of embedding megabytes of base64.
    const { imageUploadUrl: uploadUrl } = uploadRef.current;
    if (uploadUrl) {
      quill.getModule('toolbar').addHandler('image', () => {
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = '.png,.jpg,.jpeg,.webp';
        input.onchange = async () => {
          const file = input.files && input.files[0];
          if (!file) return;

          const range = quill.getSelection(true);
          try {
            const fd = new FormData();
            fd.append('image', file);
            const res = await fetch(uploadRef.current.imageUploadUrl as string, { method: 'POST', body: fd });
            const data = await res.json();
            if (data?.status !== 'success' || !data.url) throw new Error(data?.message || 'Upload failed');

            quill.insertEmbed(range.index, 'image', uploadRef.current.resolveImageUrl(data.url), 'user');
            quill.setSelection(range.index + 1, 0);
          } catch (err: any) {
            // eslint-disable-next-line no-alert
            window.alert(err?.message || 'Image upload failed');
          }
        };
        input.click();
      });
    }

    const handleChange = () => {
      const html = quill.root.innerHTML;
      const next = html === EMPTY_HTML ? '' : html;
      lastHtmlRef.current = next;
      onChangeRef.current(next);
    };
    quill.on('text-change', handleChange);

    return () => {
      quill.off('text-change', handleChange);
      quillRef.current = null;
      host.innerHTML = '';
    };
    // Mount-only: `placeholder` and the upload config are read through refs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Parent-driven content changes (e.g. opening a different post for editing).
  useEffect(() => {
    const quill = quillRef.current;
    if (!quill || value === lastHtmlRef.current) return;
    lastHtmlRef.current = value;
    const sel = quill.getSelection();
    quill.clipboard.dangerouslyPasteHTML(value || '');
    if (sel) quill.setSelection(sel.index, sel.length);
  }, [value]);

  return (
    <div className="quill-dark">
      <div ref={hostRef} style={{ ['--quill-min-height' as any]: `${minHeight}px` }} />

      {/* Snow theme is light by default — repaint it for the admin panel. */}
      <style>{`
        .quill-dark .ql-toolbar.ql-snow {
          background: #0C0D1D;
          border: 1px solid #1E2D55;
          border-bottom: none;
          border-radius: 0.5rem 0.5rem 0 0;
        }
        .quill-dark .ql-container.ql-snow {
          background: #0C0D1D;
          border: 1px solid #1E2D55;
          border-radius: 0 0 0.5rem 0.5rem;
          color: #F9F9F9;
          font-family: inherit;
          font-size: 14px;
        }
        .quill-dark .ql-editor { min-height: var(--quill-min-height, 320px); }
        /* Safety net for colours that slip past the paste matcher — without this
           black-on-black pasted text is invisible in the editor. */
        .quill-dark .ql-editor [style*="color"] {
          color: inherit !important;
          background-color: transparent !important;
        }
        .quill-dark .ql-editor.ql-blank::before { color: #878AA2; font-style: normal; }
        .quill-dark .ql-snow .ql-stroke { stroke: #878AA2; }
        .quill-dark .ql-snow .ql-fill { fill: #878AA2; }
        .quill-dark .ql-snow .ql-picker { color: #878AA2; }
        .quill-dark .ql-snow .ql-picker-options {
          background: #0E1831;
          border-color: #1E2D55;
        }
        .quill-dark .ql-snow button:hover .ql-stroke,
        .quill-dark .ql-snow button.ql-active .ql-stroke,
        .quill-dark .ql-snow .ql-picker-label:hover,
        .quill-dark .ql-snow .ql-picker-item:hover {
          stroke: #2B6EF5;
          color: #2B6EF5;
        }
        .quill-dark .ql-snow button:hover .ql-fill,
        .quill-dark .ql-snow button.ql-active .ql-fill { fill: #2B6EF5; }
        .quill-dark .ql-editor a { color: #2B6EF5; }
        .quill-dark .ql-editor blockquote { border-left: 3px solid #2B6EF5; color: #878AA2; }
        .quill-dark .ql-editor pre.ql-syntax { background: #0E1831; color: #F9F9F9; }
        .quill-dark .ql-editor img { max-width: 100%; border-radius: 0.5rem; }
        .quill-dark .ql-snow .ql-tooltip {
          background: #0E1831;
          border-color: #1E2D55;
          color: #F9F9F9;
          box-shadow: none;
        }
        .quill-dark .ql-snow .ql-tooltip input[type=text] {
          background: #0C0D1D;
          border-color: #1E2D55;
          color: #F9F9F9;
        }
      `}</style>
    </div>
  );
}
